import {
  DataTable,
  DataTableColumn,
  DateRange,
  DateRangeInput,
  DATE_FORMATTER_STR,
  DATE_FORMAT_STR,
  formatDate,
  Column,
  getRawDate,
  Row,
  Select,
  useLoading,
  ValueOpt,
  PriceFormatter,
  isBefore,
  StatsGameFormatter,
} from "best-common-react";
import React, { useEffect, useState } from "react";
import { getCreditCardReportData } from "../../../api/RequesTixApi";
import { useDropdowns } from "../../../contexts/DropdownsContext";
import { CreditCardReportDTO } from "../../../types/CreditCardReport";
import { previousMonth, yesterday } from "../../../util/DateObjUtil";
import { getTeamOption } from "../../../util/DropdownUtil";
import { ReportHandlerTypeProps } from "../ReportHandler";
import { StatsTeam } from "best-common-react";

const Columns: DataTableColumn<CreditCardReportDTO>[] = [
  {
    name: "Requester",
    key: "requesterName",
    minWidth: 200,
  },
  {
    name: "Department",
    key: "departmentName",
    minWidth: 150,
  },
  {
    name: "Total Ticket Cost",
    key: "ticketCost",
    readonlyFormatter: PriceFormatter,
    width: 150,
  },
  {
    name: "Total Order Cost",
    key: "orderCost",
    readonlyFormatter: PriceFormatter,
    width: 150,
  },
  {
    name: "Project",
    key: "projectName",
    minWidth: 250,
  },
  {
    name: "Business",
    key: "businessName",
    minWidth: 150,
  },
  {
    name: "Quantity",
    key: "quantity",
    width: 100,
  },
  {
    name: "Pay By",
    key: "payBy",
    minWidth: 250,
  },
  {
    name: "Game",
    key: "game",
    minWidth: 350,
    readonlyFormatter: StatsGameFormatter,
  },
];

const CreditCardReport = ({ reportData, setReportData, setCanExport }: ReportHandlerTypeProps) => {
  const { setLoading } = useLoading();
  const { startDate, endDate, homeTeamId } = reportData;
  const { teamsOptions } = useDropdowns();
  const [dateRange, setDateRange] = useState<DateRange<Date>>({
    start: !!startDate ? getRawDate(startDate, DATE_FORMAT_STR) : previousMonth,
    end: !!endDate ? getRawDate(endDate, DATE_FORMAT_STR) : yesterday,
  });
  const [reportDataList, setReportDataList] = useState<CreditCardReportDTO[]>([]);

  const getData = async (startDate: string, endDate: string, homeTeamId?: number) => {
    try {
      setLoading(true);
      const result: CreditCardReportDTO[] = await getCreditCardReportData(startDate, endDate, homeTeamId);
      setReportDataList(result);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const onChange = (key: keyof typeof reportData, data: number | undefined) => {
    setReportData((prev) => ({ ...prev, [key]: data }));
  };

  const updateDate = ({ start, end }: DateRange<Date>) => {
    if (!!start && !!end) {
      setReportData((prev) => ({
        ...prev,
        startDate: formatDate(start, DATE_FORMATTER_STR),
        endDate: formatDate(end, DATE_FORMATTER_STR),
      }));
    }
  };

  useEffect(() => {
    const newDate: DateRange<Date> = {
      start: startDate ? getRawDate(startDate, DATE_FORMAT_STR) : previousMonth,
      end: endDate ? getRawDate(endDate, DATE_FORMAT_STR) : yesterday,
    };
    setDateRange(newDate);
    const canExport = !!newDate.start && !!newDate.end && isBefore(newDate.start, newDate.end);
    setCanExport(canExport);
    if (canExport) {
      const startDateStr = formatDate(newDate.start!, DATE_FORMATTER_STR);
      const endDateStr = formatDate(newDate.end!, DATE_FORMATTER_STR);
      void getData(startDateStr, endDateStr, homeTeamId);
    }
  }, [startDate, endDate, homeTeamId, setCanExport]);

  return (
    <div>
      <Row>
        <Column width={4}>
          <DateRangeInput
            id="credit-card-date-range"
            label="Date Range"
            required
            gutterBottom
            value={dateRange}
            onChange={updateDate}
            withPortal
            popoverPlacement="bottom-end"
            maxRange={{
              months: 1,
            }}
          />
        </Column>
        <Column width={4}>
          <Select
            id="homeTeam"
            label="Home Team"
            options={teamsOptions}
            value={getTeamOption(homeTeamId, teamsOptions)}
            onChange={(value: ValueOpt<StatsTeam>) => {
              onChange("homeTeamId", value?.value?.id);
            }}
          />
        </Column>
      </Row>
      <DataTable className="bcr-mt-2" data={reportDataList} columns={Columns} />
    </div>
  );
};

export default CreditCardReport;
